import React, { useState } from 'react';
import { UsaStates } from 'usa-states';
import { useTheme } from '../context/ThemeContext';

interface UpdatesSignupProps {
  source?: string;
  variant?: 'inline' | 'modal';
  onSuccess?: () => void;
}

const MAX_NAME_LENGTH = 80;
const MAX_EMAIL_LENGTH = 120;
const MAX_CITY_LENGTH = 80;

const removeControlCharacters = (input: string) =>
  Array.from(input)
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code >= 32 && code !== 127;
    })
    .join('');

const stripDangerousChars = (input: string) =>
  removeControlCharacters(input).replace(/[<>]/g, '');

const sanitizeTextField = (input: string, maxLength: number) =>
  stripDangerousChars(input).slice(0, maxLength);

const sanitizeEmailField = (input: string) =>
  stripDangerousChars(input).replace(/\s+/g, '').slice(0, MAX_EMAIL_LENGTH);

const majorCitiesByState: { [key: string]: string[] } = {
  'AL': ['Birmingham', 'Montgomery', 'Mobile', 'Huntsville'],
  'AK': ['Anchorage', 'Fairbanks', 'Juneau'],
  'AZ': ['Phoenix', 'Tucson', 'Mesa', 'Scottsdale'],
  'AR': ['Little Rock', 'Fort Smith', 'Fayetteville'],
  'CA': ['Los Angeles', 'San Francisco', 'San Diego', 'San Jose', 'Sacramento', 'Oakland', 'Fresno'],
  'CO': ['Denver', 'Colorado Springs', 'Aurora', 'Fort Collins'],
  'CT': ['Hartford', 'New Haven', 'Stamford', 'Bridgeport'],
  'DE': ['Wilmington', 'Dover'],
  'FL': ['Miami', 'Orlando', 'Tampa', 'Jacksonville', 'Fort Lauderdale', 'Tallahassee'],
  'GA': ['Atlanta', 'Augusta', 'Columbus', 'Savannah'],
  'HI': ['Honolulu', 'Hilo'],
  'ID': ['Boise', 'Idaho Falls'],
  'IL': ['Chicago', 'Aurora', 'Rockford', 'Peoria'],
  'IN': ['Indianapolis', 'Fort Wayne', 'Evansville'],
  'IA': ['Des Moines', 'Cedar Rapids', 'Davenport'],
  'KS': ['Wichita', 'Overland Park', 'Kansas City', 'Topeka'],
  'KY': ['Louisville', 'Lexington', 'Bowling Green'],
  'LA': ['New Orleans', 'Baton Rouge', 'Shreveport', 'Lafayette'],
  'ME': ['Portland', 'Augusta', 'Bangor'],
  'MD': ['Baltimore', 'Annapolis', 'Frederick'],
  'MA': ['Boston', 'Worcester', 'Springfield', 'Cambridge'],
  'MI': ['Detroit', 'Grand Rapids', 'Warren', 'Sterling Heights'],
  'MN': ['Minneapolis', 'Saint Paul', 'Rochester', 'Duluth'],
  'MS': ['Jackson', 'Gulfport', 'Southaven'],
  'MO': ['Kansas City', 'Saint Louis', 'Springfield', 'Columbia'],
  'MT': ['Billings', 'Missoula', 'Great Falls'],
  'NE': ['Omaha', 'Lincoln', 'Bellevue'],
  'NV': ['Las Vegas', 'Henderson', 'Reno', 'Carson City'],
  'NH': ['Manchester', 'Nashua', 'Concord'],
  'NJ': ['Newark', 'Jersey City', 'Paterson', 'Elizabeth'],
  'NM': ['Albuquerque', 'Las Cruces', 'Rio Rancho'],
  'NY': ['New York City', 'Buffalo', 'Rochester', 'Yonkers', 'Syracuse', 'Albany'],
  'NC': ['Charlotte', 'Raleigh', 'Greensboro', 'Durham', 'Winston-Salem'],
  'ND': ['Fargo', 'Bismarck', 'Grand Forks'],
  'OH': ['Columbus', 'Cleveland', 'Cincinnati', 'Toledo', 'Akron'],
  'OK': ['Oklahoma City', 'Tulsa', 'Norman'],
  'OR': ['Portland', 'Salem', 'Eugene', 'Gresham'],
  'PA': ['Philadelphia', 'Pittsburgh', 'Allentown', 'Erie'],
  'RI': ['Providence', 'Warwick', 'Cranston'],
  'SC': ['Columbia', 'Charleston', 'North Charleston'],
  'SD': ['Sioux Falls', 'Rapid City', 'Aberdeen'],
  'TN': ['Nashville', 'Memphis', 'Knoxville', 'Chattanooga'],
  'TX': ['Houston', 'San Antonio', 'Dallas', 'Austin', 'Fort Worth', 'El Paso'],
  'UT': ['Salt Lake City', 'West Valley City', 'Provo'],
  'VT': ['Burlington', 'Montpelier', 'South Burlington'],
  'VA': ['Virginia Beach', 'Norfolk', 'Chesapeake', 'Richmond', 'Newport News'],
  'WA': ['Seattle', 'Spokane', 'Tacoma', 'Vancouver', 'Bellevue'],
  'WV': ['Charleston', 'Huntington', 'Morgantown'],
  'WI': ['Milwaukee', 'Madison', 'Green Bay'],
  'WY': ['Cheyenne', 'Casper', 'Laramie']
};

const UpdatesSignup: React.FC<UpdatesSignupProps> = ({ 
  source = 'website', 
  variant = 'inline',
  onSuccess 
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [platform, setPlatform] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [emailError, setEmailError] = useState('');
  const { dsColors, colors } = useTheme();

  const usaStates = new UsaStates();
  const usStates = usaStates.states;
  const availableCities = selectedState ? majorCitiesByState[selectedState] || [] : [];

  const heroPrimary = colors.textPrimary;
  const heroSecondary = colors.textSecondary;
  const cardBg = colors.surface;
  const cardBorder = colors.border;

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = sanitizeEmailField(e.target.value);
    setEmail(value);
    
    if (emailError) {
      setEmailError('');
    }
    
    if (value.trim() && !validateEmail(value.trim())) {
      setEmailError('Please enter a valid email address');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEmail(email.trim())) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);

    try {
      const sanitizedName = sanitizeTextField(name.trim(), MAX_NAME_LENGTH);
      const sanitizedEmail = sanitizeEmailField(email.trim().toLowerCase());
      const sanitizedCity = sanitizeTextField(selectedCity.trim(), MAX_CITY_LENGTH);
      const sanitizedState = sanitizeTextField(selectedState.trim(), 2).toUpperCase();

      const submitData = {
        name: sanitizedName,
        email: sanitizedEmail,
        city: sanitizedCity,
        state: sanitizedState,
        motorcycle_type: 'beta-tester',
        source: source,
        platform: platform || null
      };

      let result: { success: boolean; error?: string };

      if (process.env.NODE_ENV === 'development') {
        console.log('🧪 Development mode: Simulating beta tester signup');
        console.log('Would submit:', { ...submitData, email: '[REDACTED]' });
        await new Promise(resolve => setTimeout(resolve, 1000));
        result = { success: true };
      } else {
        const response = await fetch('/.netlify/functions/add-to-waitlist', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submitData)
        });

        const data = await response.json();

        if (response.status === 409) {
          result = { success: false, error: 'duplicate_email' };
        } else if (!response.ok || !data.success) {
          result = { success: false, error: data.error || 'Failed to submit' };
        } else {
          result = { success: true };
        }
      }

      if (result.success) {
        setIsSubmitted(true);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        if (result.error === 'duplicate_email') {
          setEmailError('You\'re already signed up! We\'ll be in touch soon.');
        } else {
          setEmailError(result.error || 'Failed to sign up. Please try again.');
        }
      }
    } catch (error) {
      console.error('Signup error:', error);
      setEmailError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = name.trim() && email.trim() && selectedState && selectedCity && platform && !emailError;

  if (isSubmitted) {
    return (
      <div 
        className="p-8 rounded-xl text-center"
        style={{
          backgroundColor: `${dsColors.primary}10`,
          border: `2px solid ${dsColors.primary}`
        }}
      >
        <div className="text-5xl mb-4">🏍️</div>
        <h3 className="text-2xl font-bold mb-2" style={{ color: heroPrimary }}>
          You're In!
        </h3>
        <p style={{ color: heroSecondary }}>
          Welcome to the beta program. We'll reach out when it's time to ride.
        </p>
      </div>
    );
  }

  return (
    <div 
      className="p-8 rounded-xl"
      style={{
        backgroundColor: cardBg,
        border: `2px solid ${cardBorder}`
      }}
    >
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold mb-2" style={{ color: heroPrimary }}>
          Become a Beta Tester
        </h3>
        <p style={{ color: heroSecondary }}>
          Help shape RidePulse before launch. Get early access and provide feedback that matters.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label 
            htmlFor="beta-name" 
            className="block text-sm font-semibold mb-1"
            style={{ color: heroPrimary }}
          >
            Name
          </label>
          <input
            id="beta-name"
            type="text"
            value={name}
            onChange={(e) => setName(sanitizeTextField(e.target.value, MAX_NAME_LENGTH))}
            required
            className="w-full h-12 px-4 rounded-lg border-2 text-sm focus:outline-none focus:ring-2 transition-all"
            style={{
              backgroundColor: colors.background,
              borderColor: cardBorder,
              color: heroPrimary
            }}
            placeholder="Your name"
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label 
            htmlFor="beta-email" 
            className="block text-sm font-semibold mb-1"
            style={{ color: heroPrimary }}
          >
            Email
          </label>
          <input
            id="beta-email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            required
            className="w-full h-12 px-4 rounded-lg border-2 text-sm focus:outline-none focus:ring-2 transition-all"
            style={{
              backgroundColor: colors.background,
              borderColor: emailError ? '#ef4444' : cardBorder,
              color: heroPrimary
            }}
            placeholder="your@email.com"
            disabled={isSubmitting}
          />
          {emailError && (
            <p className="text-xs mt-1" style={{ color: '#ef4444' }}>
              {emailError}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label 
              htmlFor="beta-state" 
              className="block text-sm font-semibold mb-1"
              style={{ color: heroPrimary }}
            >
              State
            </label>
            <select
              id="beta-state"
              value={selectedState}
              onChange={(e) => {
                const raw = e.target.value;
                if (!raw) {
                  setSelectedState('');
                  setSelectedCity('');
                  return;
                }
                setSelectedState(raw.slice(0, 2).toUpperCase());
                setSelectedCity('');
              }}
              required
              className="w-full h-12 px-4 rounded-lg border-2 text-sm focus:outline-none focus:ring-2 transition-all appearance-none"
              style={{
                backgroundColor: colors.background,
                borderColor: cardBorder,
                color: selectedState ? heroPrimary : heroSecondary
              }}
              disabled={isSubmitting}
            >
              <option value="">Select State</option>
              {usStates.map((state) => (
                <option key={state.abbreviation} value={state.abbreviation}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label 
              htmlFor="beta-city" 
              className="block text-sm font-semibold mb-1"
              style={{ color: heroPrimary }}
            >
              City
            </label>
            <select
              id="beta-city"
              value={selectedCity}
              onChange={(e) => setSelectedCity(sanitizeTextField(e.target.value, MAX_CITY_LENGTH))}
              required
              disabled={!selectedState || isSubmitting}
              className="w-full h-12 px-4 rounded-lg border-2 text-sm focus:outline-none focus:ring-2 transition-all appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: colors.background,
                borderColor: cardBorder,
                color: selectedCity ? heroPrimary : heroSecondary
              }}
            >
              <option value="">
                {selectedState ? 'Select City' : 'Select State First'}
              </option>
              {availableCities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label 
            htmlFor="beta-platform" 
            className="block text-sm font-semibold mb-1"
            style={{ color: heroPrimary }}
          >
            What device do you use?
          </label>
          <select
            id="beta-platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            required
            className="w-full h-12 px-4 rounded-lg border-2 text-sm focus:outline-none focus:ring-2 transition-all appearance-none"
            style={{
              backgroundColor: colors.background,
              borderColor: cardBorder,
              color: platform ? heroPrimary : heroSecondary
            }}
            disabled={isSubmitting}
          >
            <option value="">Select Platform</option>
            <option value="ios">iOS (iPhone)</option>
            <option value="android">Android</option>
            <option value="both">Both</option>
          </select>
        </div>

        <button
          type="submit"
          disabled={isSubmitting || !isFormValid}
          className="w-full h-12 rounded-lg font-bold text-base transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: dsColors.primary,
            color: '#ffffff'
          }}
        >
          {isSubmitting ? 'Signing Up...' : 'Join the Beta'}
        </button>

        <p className="text-xs text-center" style={{ color: heroSecondary }}>
          We'll never spam you or share your info. iOS beta available now via TestFlight.
        </p>
      </form>
    </div>
  );
};

export default UpdatesSignup;
